/** Catálogo de documentación empaquetada (Tutorial / Biblioteca CMS). */

export const DOCS_ROUTE = "/admin/biblioteca";
export const DOCS_LEGACY_ROUTE = "/admin/documentacion";

export const DOCS_SECTION_IDS = ["producto", "usuario", "tecnico"];

export const DOCS_SECTION_LABELS = {
  producto: "Producto",
  tecnico: "Manual técnico",
  usuario: "Manual de usuario",
};

export const DOCS_SECTION_DEFAULTS = {
  producto: "README.md",
  usuario: "usuario/README.md",
  tecnico: "tecnico/README.md",
};

export const DEFAULT_DOC_ID = DOCS_SECTION_DEFAULTS.usuario;

export const NAV_LIBRARY_ITEMS = [
  {
    id: "producto",
    label: "Producto",
    route: `${DOCS_ROUTE}/producto`,
    adminOnly: true,
  },
  {
    id: "usuario",
    label: "Manual de usuario",
    route: `${DOCS_ROUTE}/usuario`,
    adminOnly: false,
  },
  {
    id: "tecnico",
    label: "Manual técnico",
    route: `${DOCS_ROUTE}/tecnico`,
    adminOnly: true,
  },
];

const GROUP_LABELS = {
  cms: "Contenido del sitio",
  crm: "CRM y calidad",
  gestion: "Gestión",
};

const rawDocs = import.meta.glob("../../docs/**/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
});

function normalizeViteKey(key) {
  return key.replace(/\\/g, "/");
}

function toDocId(viteKey) {
  const normalized = normalizeViteKey(viteKey);
  const marker = "/docs/";
  const idx = normalized.lastIndexOf(marker);
  if (idx < 0) return null;
  const id = normalized.slice(idx + marker.length);
  if (!id) return null;
  return id;
}

function titleFromMarkdown(source, id) {
  const heading = source.match(/^#\s+(.+)$/m);
  let title = heading?.[1]?.trim();
  if (!title) {
    const file = id.split("/").pop() ?? id;
    title = file.replace(/\.md$/i, "").replace(/-/g, " ");
  }

  title = title
    .replace(/^Manual técnico\s*[—–\-]\s*/i, "")
    .replace(/^Manual de usuario\s*[—–\-]\s*/i, "")
    .replace(/\s*[—–\-]\s*CMS Website$/i, "")
    .replace(/\s*[—–\-]\s*AGDYSA$/i, "")
    .trim();

  return title || id;
}

export function docsSectionOf(id) {
  if (id.startsWith("tecnico/")) return "tecnico";
  if (id.startsWith("usuario/")) return "usuario";
  return "producto";
}

function groupOf(id, section) {
  if (section === "producto") return "";
  const rest = id.slice(section.length + 1);
  const slash = rest.indexOf("/");
  if (slash < 0) return "";
  return rest.slice(0, slash);
}

function sortKey(article) {
  const file = article.id.split("/").pop()?.toLowerCase() ?? article.id;
  const prefix = file === "readme.md" ? "0" : file.startsWith("00-") ? "1" : "2";
  return `${article.section}-${article.group}-${prefix}-${article.id}`;
}

function buildCatalog() {
  const articles = [];
  for (const [key, source] of Object.entries(rawDocs)) {
    const id = toDocId(key);
    if (!id || typeof source !== "string") continue;
    const section = docsSectionOf(id);
    articles.push({
      id,
      title: titleFromMarkdown(source, id),
      section,
      group: groupOf(id, section),
      source,
    });
  }
  articles.sort((a, b) => sortKey(a).localeCompare(sortKey(b), "es"));
  return articles;
}

export const DOCS_CATALOG = buildCatalog();

export const DOCS_BY_ID = Object.fromEntries(DOCS_CATALOG.map((article) => [article.id, article]));

export function isDocsSectionId(value) {
  return value === "producto" || value === "usuario" || value === "tecnico";
}

export function isDocsAdmin(profile) {
  const role = profile?.role;
  return role === "owner" || role === "admin";
}

export function canAccessDocsSection(profile, section) {
  if (!profile) return false;
  if (section === "usuario") return true;
  return isDocsAdmin(profile);
}

export function libraryItemsForUser(profile) {
  return NAV_LIBRARY_ITEMS.filter((item) => canAccessDocsSection(profile, item.id));
}

export function groupLabel(group) {
  if (!group) return "General";
  return GROUP_LABELS[group] ?? group;
}

export function buildDocsTree(articles) {
  const sectionOrder = ["producto", "usuario", "tecnico"];
  return sectionOrder
    .map((sectionId) => {
      const inSection = articles.filter((a) => a.section === sectionId);
      const groupIds = [];
      for (const article of inSection) {
        if (!groupIds.includes(article.group)) groupIds.push(article.group);
      }
      return {
        id: sectionId,
        label: DOCS_SECTION_LABELS[sectionId],
        groups: groupIds.map((group) => ({
          id: group,
          label: groupLabel(group),
          articles: inSection.filter((a) => a.group === group),
        })),
      };
    })
    .filter((section) => section.groups.some((g) => g.articles.length > 0));
}

export function filterDocsCatalog(query, articles = DOCS_CATALOG) {
  const q = query.trim().toLowerCase();
  if (!q) return articles;
  return articles.filter((article) => {
    const haystack = `${article.title}\n${article.id}\n${article.source.slice(0, 4000)}`.toLowerCase();
    return haystack.includes(q);
  });
}

export function docsHref(id, hash = "") {
  const h = hash && !hash.startsWith("#") ? `#${hash}` : hash;
  return `${DOCS_ROUTE}/${docsSectionOf(id)}?doc=${encodeURIComponent(id)}${h}`;
}

export function docsSectionPath(section, docId) {
  const id = docId ?? DOCS_SECTION_DEFAULTS[section];
  return `${DOCS_ROUTE}/${section}?doc=${encodeURIComponent(id)}`;
}
